import { useContext } from "react"
import { skipToken } from "@reduxjs/toolkit/query"
import { useAppSelector } from "@/store"
import type { BenefitDetailDto } from "@/lib/benefit-types"
import type { ApplicationEligibilityProgramOption } from "@/lib/eligibility-selection"
import { benefitApi } from "./benefitApi"
import type { BenefitProgramReadArg } from "./benefitEndpointDefinitions"
import { BenefitEditorContext } from "./benefitEditorContext"
import { benefitReadKey } from "./benefitReadSlice"

const emptyOptions: ApplicationEligibilityProgramOption[] = []

/** Independent saved reads. A local read identity prevents a pre-refresh child response
 * from satisfying a post-refresh observer; it does NOT make the reads a SQL snapshot.
 * Standalone compatibility callers retain their saved token/date dependency contract.
 */
function useProgramReadArg(benefit: BenefitDetailDto): BenefitProgramReadArg | typeof skipToken {
  const context = useContext(BenefitEditorContext)
  const scopeKey = useAppSelector(state => state.staffSession.scopeKey)
  const workspace = benefitApi.endpoints.getBenefitWorkspace.useQueryState(context && scopeKey
    ? { scopeKey, benefitId: benefit.id } : skipToken)
  const freshness = useAppSelector(state => scopeKey && context
    ? state.benefitReads.resources[benefitReadKey({ scopeKey, id: benefit.id })] : undefined)
  if (!scopeKey) return skipToken
  if (context && (context.address.scopeKey !== scopeKey || context.address.benefitId !== benefit.id
    || freshness?.phase !== "current" || workspace.isFetching || workspace.isError
    || !workspace.requestId || workspace.currentData?.benefit !== benefit)) return skipToken
  return { scopeKey, benefitId: benefit.id, observedConcurrencyToken: benefit.concurrencyToken ?? null,
    observedApplicationDate: benefit.applicationDate, workspaceReadRequestId: context ? workspace.requestId! : null }
}

/** Legacy actions remain an independent read, not part of the coherent workspace.
 * Never reuse an earlier list while the root/authority/read-cycle dependency changes.
 */
export function useBenefitLifecycleTransitions(benefit: BenefitDetailDto, enabled: boolean) {
  const dependency = useProgramReadArg(benefit)
  const args = enabled ? dependency : skipToken
  const query = benefitApi.useGetBenefitLifecycleTransitionsQuery(args)
  const loading = args !== skipToken && (query.isUninitialized || query.isFetching)
  return {
    readback: args !== skipToken && !loading && !query.isError ? query.currentData ?? null : null,
    loading,
    error: args === skipToken || query.isError ? "Lifecycle actions are unavailable. Refresh saved data before changing the application." : null,
    canRetry: args !== skipToken && !loading,
    retry: () => { if (args !== skipToken && !loading) void query.refetch() },
  }
}

export function useBenefitTargetYear(benefit: BenefitDetailDto) {
  const args = useProgramReadArg(benefit)
  const query = benefitApi.useGetBenefitTargetYearQuery(args)
  const loading = args !== skipToken && (query.isUninitialized || query.isFetching)
  return {
    readback: args !== skipToken && !loading && !query.isError ? query.currentData ?? null : null,
    loading,
    error: args === skipToken || query.isError ? "Target-year choices could not be loaded. Reload before changing the application." : null,
  }
}

export function useBenefitEligibilityOptions(benefit: BenefitDetailDto) {
  const args = useProgramReadArg(benefit)
  const query = benefitApi.useGetBenefitEligibilityOptionsQuery(args)
  const loading = args !== skipToken && (query.isUninitialized || query.isFetching)
  return {
    options: args !== skipToken && !loading && !query.isError ? query.currentData ?? emptyOptions : emptyOptions,
    loading,
    error: args === skipToken || query.isError ? "Benefit catalog could not be loaded. Reload before changing requested benefits." : null,
  }
}

export function useBenefitCalculationReads(benefit: BenefitDetailDto) {
  const args = useProgramReadArg(benefit)
  const catalog = benefitApi.useGetBenefitEligibilityOptionsQuery(args)
  const requirements = benefitApi.useGetBenefitCalculationReadinessQuery(args)
  const loading = args !== skipToken && [catalog, requirements].some(query => query.isUninitialized || query.isFetching)
  const available = args !== skipToken && !loading && !catalog.isError && !requirements.isError
    && !!catalog.currentData && !!requirements.currentData
  // Matching years are a necessary guard, not proof of cross-query coherence. The
  // server still checks the unchanged calculation request against current facts.
  const mismatch = available && catalog.currentData!.some(option => option.programYear !== requirements.currentData!.programYear)
  const error = args === skipToken || catalog.isError || requirements.isError
    ? "Program requirements could not be loaded. Reload before calculating eligibility."
    : mismatch ? "Program requirements and catalog years differ. Reload before calculating eligibility." : null
  return {
    options: available && !error ? catalog.currentData! : emptyOptions,
    readiness: available && !error ? requirements.currentData! : null,
    loading, error,
    canRetry: args !== skipToken && !loading,
    retry: () => {
      if (args !== skipToken && !loading) { void catalog.refetch(); void requirements.refetch() }
    },
  }
}
